import { useQuery } from '@tanstack/react-query';

import { getDepartments } from '@/services/departmentService';
import type { DepartmentDto } from '@/types/department';

const CASCADE_PAGE_SIZE = 500;

interface UseDepartmentsForCascadeResult {
  departments: DepartmentDto[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  hasMore: boolean;
}

/**
 * Fetches the active-department list for the EmployeeForm Department→JobTitle
 * cascade. Capped at {@link CASCADE_PAGE_SIZE}; if a tenant exceeds the cap the
 * `hasMore` flag is true and the dropdown shows the first 500. Switch to a
 * search-driven AsyncSelect if support tickets ever land on this.
 *
 * No `hasPermission()` gate on `enabled` — perms are backend-authoritative per
 * frontend/.claude/CLAUDE.md; consumers handle 403 with `isForbiddenError`.
 */
export function useDepartmentsForCascade(): UseDepartmentsForCascadeResult {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['departments-cascade'],
    queryFn: () =>
      getDepartments({ page: 0, size: CASCADE_PAGE_SIZE }, { filters: { status: 'ACTIVE' } }),
  });

  return {
    departments: data?.content ?? [],
    isLoading,
    isError,
    error,
    hasMore: (data?.totalElements ?? 0) > CASCADE_PAGE_SIZE,
  };
}
